import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/lux";
import { MoneyField } from "./fields";
import { solveFor, paymentFor, computeWorksheet, money } from "@/dealership/lib/desk";
import { toast } from "sonner";

const VARS = [
  { key: "down", label: "Down payment" },
  { key: "price", label: "Selling price" },
  { key: "term", label: "Term" },
  { key: "rate", label: "Rate" },
];

const TERM_OPTIONS = [24, 36, 42, 48, 54, 60, 66, 72, 78, 84, 90, 96];

/** Work backwards from a target payment by solving for one variable. */
export default function SolveDialog({ open, onOpenChange, ws, settings, onApply }) {
  const [target, setTarget] = useState(0);
  const [variable, setVariable] = useState("down");
  const [result, setResult] = useState(null);
  const fin = ws.finance || {};

  const run = () => {
    if (!(target > 0)) return toast.error("Enter the payment the customer wants");
    const finalize = (label, patch, infeasible) => {
      const payment = paymentFor(ws, settings, patch);
      if (payment > target + 0.5) return setResult({ ok: false, msg: infeasible });
      return setResult({ ok: true, label, patch, payment });
    };
    if (variable === "term") {
      const hit = TERM_OPTIONS.find((t) => paymentFor(ws, settings, { finance: { ...fin, term_months: t } }) <= target);
      if (!hit) return setResult({ ok: false, msg: "Even 96 months doesn't reach that payment — adjust the price or down payment." });
      const patch = { finance: { ...fin, term_months: hit } };
      return setResult({ ok: true, label: `${hit} months`, patch, payment: paymentFor(ws, settings, patch) });
    }
    if (variable === "down") {
      const totals = computeWorksheet(ws, settings);
      const hi = Math.max(totals.amount_financed + Number(fin.cash_down || 0), 1000);
      const v = solveFor({ ws, settings, target, lo: 0, hi, decreasing: true, apply: (x) => ({ finance: { ...fin, cash_down: x } }) });
      const rounded = Math.ceil(v / 10) * 10;
      return finalize(money(rounded), { finance: { ...fin, cash_down: rounded } },
        "Even paying the whole balance down doesn't reach that payment — try a longer term or a lower rate.");
    }
    if (variable === "price") {
      const v = solveFor({ ws, settings, target, lo: 0, hi: Math.max(Number(ws.selling_price) * 2, 20000), decreasing: false, apply: (x) => ({ selling_price: x }) });
      const rounded = Math.floor(v / 10) * 10;
      return finalize(money(rounded), { selling_price: rounded },
        "That payment isn't reachable even at a $0 selling price — the fees, products or negative equity alone carry more than that. Try a longer term or more money down.");
    }
    const v = solveFor({ ws, settings, target, lo: 0, hi: 35, decreasing: false, apply: (x) => ({ finance: { ...fin, apr: x } }) });
    const rounded = Math.round(v * 100) / 100;
    return finalize(`${rounded}%`, { finance: { ...fin, apr: rounded } },
      "Even at 0% that payment isn't reachable — try a longer term, more money down, or a lower price.");
  };

  const apply = () => {
    onApply(result.patch);
    onOpenChange(false);
    setResult(null);
    toast.success("Structure applied");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setResult(null); }}>
      <DialogContent className="max-w-lg" data-testid="desk-solve-dialog">
        <DialogHeader>
          <DialogTitle>Solve for a target payment</DialogTitle>
          <DialogDescription>Work backwards from the payment the customer wants.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <div className="label-mono mb-2">Customer wants this {(fin.frequency || "biweekly").replace("semimonthly", "semi-monthly").replace("biweekly", "bi-weekly")} payment</div>
            <MoneyField value={target} onChange={setTarget} testid="solve-target" />
          </div>
          <div>
            <div className="label-mono mb-2">Solve for</div>
            <div className="flex gap-2 flex-wrap">
              {VARS.map((v) => (
                <Chip key={v.key} active={variable === v.key} onClick={() => { setVariable(v.key); setResult(null); }} testid={`solve-var-${v.key}`}>{v.label}</Chip>
              ))}
            </div>
          </div>
          {result && (
            <div className={`rounded-2xl border p-4 ${result.ok ? "border-primary/30 bg-primary/[0.05]" : "border-red-500/30 bg-red-500/[0.06]"}`} data-testid="solve-result">
              {result.ok ? (
                <>
                  <div className="label-mono">Required {VARS.find((v) => v.key === variable).label.toLowerCase()}</div>
                  <div className="font-heading text-3xl money mt-1">{result.label}</div>
                  <div className="text-xs text-muted-foreground mt-2">Payment lands at {money(result.payment)}</div>
                </>
              ) : (
                <p className="text-sm text-red-300">{result.msg}</p>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button variant="outline" onClick={run} data-testid="solve-run">Solve</Button>
            <Button disabled={!result?.ok} onClick={apply} data-testid="solve-apply">Apply to worksheet</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
